import { useId, useState, type ReactNode } from "react";
import type { FunctionReturnType } from "convex/server";
import { useMutation, useQuery } from "convex/react";
import { Archive, ArchiveRestore, Copy, Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Label,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { dateCourte } from "@repo/utils";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  DialogueDupliquer,
  DialogueRenommer,
  DialogueSupprimer,
  type EnTete,
} from "./DialoguesScorecard";
import { messageErreur } from "./edition";
import { BadgeStatut } from "./Publication";

/**
 * Où lister : les scorecards d'une organisation (sans `organisationId` :
 * celle de l'utilisateur, portail client), ou les modèles globaux
 * (super-admin).
 */
/** Une ligne de la liste, telle que le serveur la sert. */
export type LigneScorecard = FunctionReturnType<
  typeof api.scorecards.listerScorecards
>[number];

export type PorteeScorecards =
  | { type: "organisation"; organisationId?: Id<"organisations"> }
  | { type: "modeles" };

/**
 * Scorecards avec statut de publication et archivage ; duplication pour
 * celles d'une organisation déjà publiées (un modèle global ne se duplique
 * pas), suppression pour les brouillons jamais publiés.
 */
export function ListeScorecards({
  portee,
  lienScorecard,
  onOuvrir,
  messageVide = "Aucune scorecard. Les scorecards sont attribuées à votre organisation par Evaluans.",
  actionsLigne,
}: {
  portee: PorteeScorecards;
  lienScorecard: (id: Id<"scorecards">) => string;
  /** Après une duplication : ouvrir la copie. */
  onOuvrir: (id: Id<"scorecards">) => void;
  messageVide?: string;
  /** Actions propres à un portail, ajoutées à droite de chaque ligne. */
  actionsLigne?: (ligne: LigneScorecard) => ReactNode;
}) {
  const idArchivees = useId();
  const [inclureArchivees, setInclureArchivees] = useState(false);
  const modeles = portee.type === "modeles";
  const scorecardsOrg = useQuery(
    api.scorecards.listerScorecards,
    portee.type === "organisation"
      ? { organisationId: portee.organisationId, inclureArchivees }
      : "skip",
  );
  const listeModeles = useQuery(
    api.scorecards.listerModeles,
    modeles ? { inclureArchives: inclureArchivees } : "skip",
  );
  const scorecards = modeles ? listeModeles : scorecardsOrg;
  const archiver = useMutation(api.scorecards.archiverScorecard);
  const desarchiver = useMutation(api.scorecards.desarchiverScorecard);
  const [aDupliquer, setADupliquer] = useState<EnTete | null>(null);
  const [aRenommer, setARenommer] = useState<EnTete | null>(null);
  const [aSupprimer, setASupprimer] = useState<EnTete | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const basculerArchive = async (id: Id<"scorecards">, archivee: boolean) => {
    setErreur(null);
    try {
      await (archivee ? desarchiver : archiver)({ scorecardId: id });
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };

  return (
    // `contain: inline-size` : comme l'éditeur, la largeur vient du parent (le
    // `body` du portail client est une grille qui suivrait celle du tableau).
    <Card className="[contain:inline-size]">
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-center gap-2">
          <Checkbox
            id={idArchivees}
            checked={inclureArchivees}
            onCheckedChange={(v) => setInclureArchivees(v === true)}
          />
          <Label htmlFor={idArchivees}>
            {modeles ? "Afficher les modèles archivés" : "Afficher les scorecards archivées"}
          </Label>
        </div>
        {erreur && (
          <p role="alert" className="text-sm text-destructive">
            {erreur}
          </p>
        )}

        {scorecards === undefined ? (
          <div className="flex justify-center p-8">
            <Loader />
          </div>
        ) : scorecards.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {messageVide}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{modeles ? "Modèle" : "Scorecard"}</TableHead>
                <TableHead>{modeles ? "Slug" : "Modèle d'origine"}</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Modifiée le</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scorecards.map((s) => {
                const archivee = s.archivedAt !== undefined;
                const brouillon = s.numeroVersionPubliee === null;
                return (
                  <TableRow key={s._id}>
                    <TableCell>
                      <Link
                        to={lienScorecard(s._id)}
                        className="font-medium hover:text-charte-accent hover:underline"
                      >
                        {s.titre}
                      </Link>
                      {archivee && (
                        <Badge variant="secondary" className="ml-2">
                          Archivée
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {(modeles ? s.slug : s.modeleSlug) ?? "—"}
                    </TableCell>
                    <TableCell>
                      <BadgeStatut
                        numeroCourant={s.numeroVersionCourante}
                        numeroPublie={s.numeroVersionPubliee}
                      />
                    </TableCell>
                    <TableCell className="tabular-nums">{s.numeroVersionCourante}</TableCell>
                    <TableCell className="tabular-nums">{dateCourte(s.modifieLe)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={archivee}
                          title={archivee ? "Désarchivez-la pour la renommer" : undefined}
                          onClick={() => setARenommer(s)}
                        >
                          <Pencil /> Renommer
                        </Button>
                        {!modeles && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={brouillon}
                            title={brouillon ? "Publiez la scorecard avant de la dupliquer" : undefined}
                            onClick={() => setADupliquer(s)}
                          >
                            <Copy /> Dupliquer
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void basculerArchive(s._id, archivee)}
                        >
                          {archivee ? <ArchiveRestore /> : <Archive />}
                          {archivee ? "Désarchiver" : "Archiver"}
                        </Button>
                        {brouillon && (
                          <Button variant="ghost" size="sm" onClick={() => setASupprimer(s)}>
                            <Trash2 /> Supprimer
                          </Button>
                        )}
                        {actionsLigne?.(s)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {aDupliquer && (
        <DialogueDupliquer
          source={aDupliquer}
          onFermer={() => setADupliquer(null)}
          onDupliquee={(id) => {
            setADupliquer(null);
            onOuvrir(id);
          }}
        />
      )}
      {aRenommer && (
        <DialogueRenommer source={aRenommer} onFermer={() => setARenommer(null)} />
      )}
      {aSupprimer && (
        <DialogueSupprimer source={aSupprimer} onFermer={() => setASupprimer(null)} />
      )}
    </Card>
  );
}
